/**
 * Which workspace server serves an organization.
 *
 * Most organizations are served by the shared workspace server
 * (`AMOREG_MCP_URL`). A dedicated deployment can serve one instead (IOM FSA
 * from the London replicas), and the shared server then refuses that
 * organization outright — per-thread credentials, the bound service bearer,
 * the inspector knock. So every caller that holds a credential for an
 * organization must send it to that organization's server, chosen here from
 * deployment configuration:
 *
 *   AMOREG_ORG_WORKSPACE_ORIGINS=<org-id>=https://<host>[,<org-id>=https://<host>]
 *
 * The organization is always the verified principal's; the origin is always
 * this configuration's. Nothing a request carries (an endpoint header, a URL
 * parameter) can name the host a credential is sent to. An organization that
 * is not listed keeps the caller's default address, so the map is inert until
 * it is set. A malformed map is refused outright rather than half-applied: a
 * dropped entry would quietly send an organization's work to a server that
 * refuses it.
 *
 * @module workspaceOrigins
 */

export const ORG_WORKSPACE_ORIGINS_ENV = "AMOREG_ORG_WORKSPACE_ORIGINS";

/**
 * Organization ids as every consumer compares them: Postgres prints UUIDs in
 * lowercase and the lookups are exact.
 */
export const normalizeOrganizationId = (id: string): string => id.trim().toLowerCase();

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isOrganizationUuid = (value: string): boolean => UUID_PATTERN.test(value);

/** A bare `https://host[:port]` origin: no path, query, fragment or trailing slash. */
export const isHttpsOrigin = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.origin === value;
  } catch {
    return false;
  }
};

/**
 * `<org-id>=<origin>` pairs, organization ids normalized, blanks dropped.
 * Lenient on purpose: a malformed entry is kept (an entry without `=` maps to
 * `""`) for the caller's validation to refuse, never silently dropped.
 */
export const parseOrgOriginPairs = (raw: string): Readonly<Record<string, string>> =>
  Object.fromEntries(
    raw
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .map((entry) => {
        const separator = entry.indexOf("=");
        return separator < 0
          ? [normalizeOrganizationId(entry), ""]
          : [normalizeOrganizationId(entry.slice(0, separator)), entry.slice(separator + 1).trim()];
      }),
  );

export class OrgWorkspaceOriginsError extends Error {
  override readonly name = "OrgWorkspaceOriginsError";
}

export type OrgWorkspaceOrigins = ReadonlyMap<string, string>;

/**
 * Parse `AMOREG_ORG_WORKSPACE_ORIGINS`. Unset or blank is the empty map.
 * Throws {@link OrgWorkspaceOriginsError} — naming the rule, never echoing the
 * value — on an entry without `=`, a non-UUID organization, an origin that is
 * not a bare https origin, or an organization listed twice.
 */
export function parseOrgWorkspaceOrigins(raw: string | null | undefined): OrgWorkspaceOrigins {
  const origins = new Map<string, string>();
  for (const entry of (raw ?? "").split(",")) {
    const trimmed = entry.trim();
    if (trimmed.length === 0) continue;
    const separator = trimmed.indexOf("=");
    if (separator < 0) {
      throw new OrgWorkspaceOriginsError(
        `${ORG_WORKSPACE_ORIGINS_ENV} entries must be <org-id>=https://<host>.`,
      );
    }
    const organizationId = normalizeOrganizationId(trimmed.slice(0, separator));
    const origin = trimmed.slice(separator + 1).trim();
    if (!isOrganizationUuid(organizationId)) {
      throw new OrgWorkspaceOriginsError(
        `${ORG_WORKSPACE_ORIGINS_ENV} contains a non-UUID organization.`,
      );
    }
    if (!isHttpsOrigin(origin)) {
      throw new OrgWorkspaceOriginsError(
        `${ORG_WORKSPACE_ORIGINS_ENV} entries must be <org-id>=https://<host> (a bare origin).`,
      );
    }
    if (origins.has(organizationId)) {
      throw new OrgWorkspaceOriginsError(
        `${ORG_WORKSPACE_ORIGINS_ENV} lists an organization more than once.`,
      );
    }
    origins.set(organizationId, origin);
  }
  return origins;
}

/**
 * The origin of the workspace server that serves `organizationId`: its
 * configured dedicated origin, else `defaultOrigin`. No organization (a
 * session that names none) is the default: the shared server then answers,
 * and an org-scoped call fails closed there.
 */
export const workspaceOriginForOrganization = (
  origins: OrgWorkspaceOrigins,
  organizationId: string | null | undefined,
  defaultOrigin: string,
): string =>
  (organizationId ? origins.get(normalizeOrganizationId(organizationId)) : undefined) ??
  defaultOrigin;
